import './composite-filter.scss'
import './film-list.scss'
import type { GridElement } from '@omnia/ui/src/lib'
import {
    FilmList as OmniaUiFilmList,
    useGetFilmsPlayingInSites,
    useLoadConfigurationComponents,
    useLogger,
} from '@omnia/ui/src/lib'
import { CompositeFilterDisplayTextPropsApi } from '@omnia/ui/src/lib/cms/components/compositeFilter/models'
import {
    type FilmListConfigPropsApi,
    FilmListDisplayTextPropsApi,
} from '@omnia/ui/src/lib/cms/components/filmList/models'
import { SingleFilterDisplayTextPropsApi } from '@omnia/ui/src/lib/cms/components/singleFilter/models'
import { FilmThumbnailMediaType } from '@vista-digital/occ-ui'
import { observer } from 'mobx-react'
import { type FC, useMemo } from 'react'
import { getBrowsingConfiguration } from '@/common/components/OmniaHeroCarousel/utils/get-browsing-configuration'
import { getSelectedCinemaGroupSiteIds } from '@/features/cinema-groups/utils/get-selected-cinema-group-site-ids'
import type { EventDetailFilmConfigPropsApi } from '@/pages/eventDetails/models'

type FilmListSortingMethod = 'Alphabetical' | 'ReleaseDate'

type FilmListValue = FilmListConfigPropsApi & {
    sortingMethod?: FilmListSortingMethod
    numberOfDaysConsideredNewRelease?: number
    isNowShowing?: boolean
}

type FilmListGridElementProps = Omit<GridElement<FilmListConfigPropsApi>, 'value'> & {
    value: FilmListValue
    config?: {
        numberOfDaysConsideredNewRelease?: number
        shouldFilterBySelectedLocation?: boolean
    }
    filmIdsOverride?: string[]
    loadingOverride?: boolean
    filterIdOverride?: string
    showGenre?: boolean
    deferredDateData?: EventDetailFilmConfigPropsApi
}

type FilmListConfigWithEventDetails = FilmListConfigPropsApi & {
    showGenre?: boolean
    deferredDateData?: EventDetailFilmConfigPropsApi
}

// eslint-disable-next-line complexity -- merging multiple configs
export const FilmList: FC<FilmListGridElementProps> = observer(props => {
    const logger = useLogger('FilmList')

    const { value, config, filterIdOverride, filmIdsOverride, loadingOverride, deferredDateData, showGenre } = props

    const { data, isPending } = useLoadConfigurationComponents({
        SingleFilterDisplayText: new SingleFilterDisplayTextPropsApi(),
        FilmListDisplayText: new FilmListDisplayTextPropsApi(),
        CompositeFilterDisplayText: new CompositeFilterDisplayTextPropsApi(),
    })

    logger.trace('Rendering', { props, data, isPending })

    const baseFilterId = 'film-list-filter-id'
    const filterId = filterIdOverride ?? baseFilterId

    const siteIds = getSelectedCinemaGroupSiteIds()
    const { filmIds: siteFilmIds, loading: siteLoading } = useGetFilmsPlayingInSites(siteIds)

    const filmIds = filmIdsOverride ?? siteFilmIds
    const loading = loadingOverride ?? siteLoading

    const browsingConfig = getBrowsingConfiguration()?.films
    const { showPlayTrailerButton } = browsingConfig?.filmList ?? {
        showPlayTrailerButton: false,
    }

    logger.trace('Rendering', { props, data, isPending, filterId, siteIds, filmIds, loading })

    const { numberOfDaysConsideredNewRelease, shouldFilterBySelectedLocation } = config ?? {}

    const omniaConfig = useMemo<FilmListConfigWithEventDetails>(
        () => ({
            ...value,
            showGenre,
            deferredDateData,
            numberOfDaysConsideredNewRelease
        }),
        [value, showGenre, deferredDateData, numberOfDaysConsideredNewRelease]
    )

    return (
        <OmniaUiFilmList
            filterId={filterId}
            filterBySelectedSites={shouldFilterBySelectedLocation}
            filmIds={filmIds}
            loading={isPending || loading}
            displayText={{
                SingleFilter: data?.SingleFilterDisplayText || new SingleFilterDisplayTextPropsApi(),
                FilmList: data?.FilmListDisplayText || new FilmListDisplayTextPropsApi(),
                CompositeFilter: data?.CompositeFilterDisplayText || new CompositeFilterDisplayTextPropsApi(),
            }}
            config={omniaConfig}
            cdnOptions={{
                filmThumbnailImage: {
                    width: 400,
                },
                censorRatingIcon: {
                    // Censor rating icons are rendered much smaller than this, but using a larger size here will allow for clearer text on devices with higher pixel density
                    height: 60,
                },
            }}
            staticConfig={{
                thumbnail: {
                    mediaType: FilmThumbnailMediaType.Poster,
                },
                playTrailerButton: {
                    show: showPlayTrailerButton,
                },
                ignoreNowShowingReleaseDateExclusion: true,
            }}
        />
    )
})
